import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Info } from "lucide-react"
import { useEffect, useState, type JSX } from "react"

import { useToast } from "@/components/ui/use-toast"
import { MultiUserSelector } from "@crm-src/components/multi-user-selector"
import usePermissions from "@crm-src/hooks/usePermissions"
import { useResellers } from "@crm-src/hooks/useResellers"
import type { Reseller } from "@crm-src/types/lead"

/** crm-api reserves 700; the form says so before the request is made rather
 * than letting a 422 explain it after. */
const RESERVED_CODE = "700"

interface ResellerEditDialogProps {
  open: boolean
  reseller: Reseller
  onClose: () => void
  onSaved: () => void | Promise<void>
}

/**
 * Edit a reseller's identity and address.
 *
 * Lifted out of the detail page when that page became a tab shell: a dialog
 * with its own form state, its own validation and its own submit is a
 * component, and leaving it inline was most of why the page was 780 lines.
 *
 * The salespeople picker shows only for `canAssignResellerSalespeople()` (the
 * `crm:resellers_salespeople` grant) — that writes an assignment, not the
 * reseller row, and crm-api gates it separately.
 */
export function ResellerEditDialog({ open, reseller, onClose, onSaved }: ResellerEditDialogProps): JSX.Element {
  const { toast } = useToast()
  const { canAssignResellerSalespeople } = usePermissions()
  const canAssign = canAssignResellerSalespeople()
  const { updateReseller } = useResellers()

  const [form, setForm] = useState({
    name: "",
    code: "",
    website: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "U.S.A",
    assigned_salespeople_ids: [] as string[],
  })
  const [codeError, setCodeError] = useState("")

  // Re-seed from the record every time the dialog OPENS, so a cancelled edit
  // does not leave stale values behind for the next one.
  useEffect(() => {
    if (!open) return
    setForm({
      name: reseller.name,
      code: reseller.code || "",
      website: reseller.website || "",
      address: reseller.address || "",
      city: reseller.city || "",
      state: reseller.state || "",
      postal_code: reseller.postal_code || "",
      country: reseller.country || "U.S.A",
      assigned_salespeople_ids: reseller.assigned_salespeople?.map((sp) => sp.id) || [],
    })
    setCodeError("")
  }, [open, reseller])

  const handleCodeChange = (code: string): void => {
    setForm((prev) => ({ ...prev, code }))
    setCodeError(code === RESERVED_CODE ? `Code '${RESERVED_CODE}' is reserved and cannot be used` : "")
  }

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    if (codeError) return

    try {
      await updateReseller(reseller.id, {
        name: form.name,
        ...(form.code ? { code: form.code } : {}),
        ...(form.website ? { website: form.website } : {}),
        ...(form.address ? { address: form.address } : {}),
        ...(form.city ? { city: form.city } : {}),
        ...(form.state ? { state: form.state } : {}),
        ...(form.postal_code ? { postal_code: form.postal_code } : {}),
        ...(form.country ? { country: form.country } : {}),
        ...(canAssign && form.assigned_salespeople_ids.length > 0
          ? { assigned_salespeople_ids: form.assigned_salespeople_ids }
          : {}),
      })
      toast({ title: "Success", description: "Reseller updated successfully" })
      await onSaved()
    } catch (error) {
      const message =
        (error as { response?: { data?: { errors?: string[] } } })?.response?.data?.errors?.join(", ") ||
        "Failed to update reseller"
      toast({ variant: "destructive", title: "Error", description: message })
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Reseller</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>Update reseller information and address.</DialogContentText>
        <Box component="form" id="reseller-form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 500 }}>
              Basic Information
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField
                id="edit-name"
                label="Reseller Name *"
                size="small"
                fullWidth
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Enter reseller name"
                required
              />
              <Box>
                <TextField
                  id="edit-reseller-code"
                  label="Reseller Code"
                  size="small"
                  fullWidth
                  value={form.code}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  placeholder="Enter 3-digit code (e.g., 123)"
                  error={!!codeError}
                  inputProps={{ maxLength: 3, pattern: "[0-9]{3}", title: "Reseller code must be exactly 3 digits" }}
                />
                <Typography sx={{ fontSize: 14, mt: 0.5, color: codeError ? "error.main" : "text.secondary" }}>
                  {codeError || `Optional 3-digit numeric code (${RESERVED_CODE} is reserved)`}
                </Typography>
              </Box>
            </Box>

            {canAssign && (
              <MultiUserSelector
                assignableFallback
                value={form.assigned_salespeople_ids}
                onValueChange={(value) => setForm({ ...form, assigned_salespeople_ids: value })}
                label="Assigned Salespeople"
                placeholder="Select salespeople..."
              />
            )}

            <TextField
              id="edit-website"
              label="Website"
              size="small"
              fullWidth
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://example.com"
              type="url"
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography component="h3" sx={{ fontSize: "1.125rem", fontWeight: 500 }}>
              Address Information
            </Typography>
            <Box
              sx={{
                bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                border: 1,
                borderColor: (t) => alpha(t.palette.primary.main, 0.2),
                borderRadius: 1,
                p: 1.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box sx={{ mt: 0.25, flexShrink: 0, color: "primary.main", display: "flex" }}>
                <Info size={16} />
              </Box>
              <Typography sx={{ fontSize: 14, color: "primary.dark" }}>
                This address will be automatically used to fill in billing information when generating invoices for
                this reseller.
              </Typography>
            </Box>
            <TextField
              id="edit-address"
              label="Street Address"
              size="small"
              fullWidth
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="Enter street address"
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField
                id="edit-city"
                label="City"
                size="small"
                fullWidth
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
                placeholder="Enter city"
              />
              <TextField
                id="edit-state"
                label="State/Province"
                size="small"
                fullWidth
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
                placeholder="Enter state or province"
              />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <TextField
                id="edit-postal_code"
                label="Postal Code"
                size="small"
                fullWidth
                value={form.postal_code}
                onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                placeholder="Enter postal code"
              />
              <TextField
                id="edit-country"
                label="Country"
                size="small"
                fullWidth
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                placeholder="Enter country"
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button type="button" variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" form="reseller-form" variant="contained" disabled={!!codeError}>
          Update Reseller
        </Button>
      </DialogActions>
    </Dialog>
  )
}
